/**
 * voice-v8 — die Messzeile `[ptt-stille]`: warum „Absenden nach Stille"
 * (nicht) ausgelöst hat. (23.09.2026, Tickets 2512 und 2420)
 *
 * ## Woher das kommt
 *
 * Martin, 23.09., 23:14: *„Absenden nach Stille gibt es immer noch nicht."*
 * Am Server steht `sendeStilleMs=3000`. Seit pre.3218 (19:30) endete genau
 * EINE Aufnahme durch Stille (19:33:34, nativ), und die war leer. Am Mac
 * endeten von 21:06 bis 22:06 vierzehn Aufnahmen, keine durch Stille. Um
 * 23:18:49 ging eine Aufnahme per Knopf raus, obwohl der Server danach
 * `endstille=3.1` maß.
 *
 * Das Protokoll konnte drei Erklärungen nicht trennen:
 *   1. Einstellung — hatte das Gerät die Schwelle überhaupt geladen?
 *   2. Pegel — unterschreitet das Grundrauschen die feste Grenze von 0,03 nie?
 *      (Browser: 0,03 ≙ rund −74 dBFS Effektivwert, nativ −48,5 dBFS.)
 *   3. Takt — kam der Pegel überhaupt oft genug an?
 *
 * ⚠️ Nur messen, nichts entscheiden. Diese Datei ändert KEIN Verhalten;
 *    die Regel steht weiter in jetzt-senden.ts. Erst die Zahlen sagen, ob
 *    die Grenze relativ zum Grundpegel gesetzt werden muss.
 */

import { PEGEL_GRENZE } from "./mic-visualizer.js";

interface StilleMessung {
  /** Pegel jedes wirklich gemessenen Bildes (0…1). */
  pegel: number[];
  /**
   * Längste Stille, die die Entscheidung hätte zählen DÜRFEN — also nach
   * der ersten Stimme (siehe `gesprochen` in jetzt-senden.ts).
   */
  laengsteMs: number;
  /** Längste Stille überhaupt, auch vor dem ersten Wort. */
  laengsteGesamtMs: number;
  /** Wie oft die Entscheidung gefragt hat (Takt 250 ms). */
  takte: number;
  /** Schwelle, die die Entscheidung zuletzt geladen hat; null = nie gefragt. */
  schwelleMs: number | null;
  /** Woher der Pegel kam; null = es kam keiner. */
  quelle: "nativ" | "browser" | null;
  /** Die zuletzt geltende Stille-Grenze (Ticket 2606: folgt dem Grundpegel). */
  grenze?: number;
  /** 07.10.2026: die zuletzt geltende Untergrenze aus der Sprache dieser Aufnahme (nur Browser-Weg). */
  sprachgrenze?: number;
}

export function neueStilleMessung(): StilleMessung {
  return {
    pegel: [],
    laengsteMs: 0,
    laengsteGesamtMs: 0,
    takte: 0,
    schwelleMs: null,
    quelle: null,
  };
}

/**
 * Ein Bild des Pegel-Visualizers festhalten.
 *
 * @param roh `null`/`undefined`, wenn nicht wirklich gemessen wurde — das
 *   Bild zählt dann nicht in die Verteilung.
 * @param nachStimme `hatGesprochen()` in der Karte — derselbe Ausdruck, mit
 *   dem die Entscheidung fragt, ob Stille schon zählen darf.
 */
export function merkeBild(
  m: StilleMessung,
  roh: number | null | undefined,
  stillMs: number,
  nachStimme: boolean,
  grenze?: number,
  sprachgrenze?: number,
): void {
  if (typeof roh === "number" && Number.isFinite(roh)) m.pegel.push(roh);
  if (typeof grenze === "number" && Number.isFinite(grenze)) m.grenze = grenze;
  if (typeof sprachgrenze === "number" && Number.isFinite(sprachgrenze)) m.sprachgrenze = sprachgrenze;
  if (stillMs > m.laengsteGesamtMs) m.laengsteGesamtMs = stillMs;
  if (nachStimme && stillMs > m.laengsteMs) m.laengsteMs = stillMs;
}

/** Quantil einer sortierten Liste. */
function quantil(sortiert: readonly number[], p: number): number {
  return sortiert[Math.floor(p * (sortiert.length - 1))] ?? 0;
}

/**
 * Die Zeile, ohne `[ptt-stille]` (das setzt meldeSpur davor).
 *
 * `schwelle` ist allein, was der Entscheidungstakt zuletzt geladen hat. Hat
 * er nie gefragt, steht „-" — kein Rückgriff auf die Einstellung am Ende,
 * sonst hätte das Feld zwei Quellen, und eine mitten in der Aufnahme
 * geänderte Einstellung sähe aus wie die, nach der entschieden wurde.
 * (23.09.2026, Nachprüfung)
 */
export function stilleZeile(
  m: StilleMessung,
  e: { gesprochen: boolean; dauerMs: number },
): string {
  const s = [...m.pegel].sort((a, b) => a - b);
  const pegel = s.length
    ? [0.1, 0.5, 0.9].map((p) => quantil(s, p).toFixed(3)).join("/")
    : "-";
  const nativ = m.quelle === "nativ" ? "ja" : m.quelle === "browser" ? "nein" : "-";
  return (
    `schwelle=${m.schwelleMs === null ? "-" : Math.round(m.schwelleMs)}` +
    ` laengste=${Math.round(m.laengsteMs)}ms` +
    ` laengsteGesamt=${Math.round(m.laengsteGesamtMs)}ms` +
    ` gesprochen=${e.gesprochen ? "ja" : "nein"}` +
    ` pegel_p10/p50/p90=${pegel}` +
    ` grenze=${(m.grenze ?? PEGEL_GRENZE).toFixed(3)}` +
    ` messungen=${s.length}` +
    ` takte=${m.takte}` +
    ` dauer=${Math.round(e.dauerMs)}ms` +
    ` nativ=${nativ}` +
    ` sprachgrenze=${m.sprachgrenze === undefined ? "-" : m.sprachgrenze.toFixed(3)}`
  );
}
